// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.

import {
  assertArgs,
  lastPathSegment,
  stripSuffix,
} from "../_common/basename.ts";
import { stripTrailingSeparators } from "../_common/strip_trailing_separators.ts";
import { isPosixPathSeparator } from "./_util.ts";

/**
 * Return the last portion of a `path`.
 * Trailing directory separators are ignored, and optional suffix is removed.
 *
 * @example Usage
 * ```ts
 * import { basename } from "@std/path/posix/basename";
 * import { assertEquals } from "@std/assert";
 *
 * assertEquals(basename("/home/user/Documents/"), "Documents");
 * assertEquals(basename("/home/user/Documents/image.png"), "image.png");
 * assertEquals(basename("/home/user/Documents/image.png", ".png"), "image");
 * ```
 *
 * @example Working with URLs
 *
 * Note: This function doesn't automatically strip hash and query parts from
 * URLs. If your URL contains a hash or query, remove them before passing the
 * URL to the function. This can be done by passing the URL to `new URL(url)`,
 * and setting the `hash` and `search` properties to empty strings.
 *
 * ```ts
 * import { basename } from "@std/path/posix/basename";
 * import { assertEquals } from "@std/assert";
 *
 * assertEquals(basename("https://deno.land/std/path/mod.ts"), "mod.ts");
 * assertEquals(basename("https://deno.land/std/path/mod.ts", ".ts"), "mod");
 * assertEquals(basename("https://deno.land/std/path/mod.ts?a=b"), "mod.ts?a=b");
 * assertEquals(basename("https://deno.land/std/path/mod.ts#header"), "mod.ts#header");
 * ```
 *
 * Note: If you are working with file URLs,
 * use the new version of `basename` from `@std/path/posix/unstable-basename`.
 *
 * @param path The path to extract the name from.
 * @param suffix The suffix to remove from extracted name.
 * @returns The extracted name.
 */
export function basename(path: string, suffix = ""): string {
  assertArgs(path, suffix);

  const lastSegment = lastPathSegment(path, isPosixPathSeparator);
  const strippedSegment = stripTrailingSeparators(
    lastSegment,
    isPosixPathSeparator,
  );
  return suffix ? stripSuffix(strippedSegment, suffix) : strippedSegment;
}

// denoCacheMetadata={"headers":{"last-modified":"Fri, 01 Nov 2024 10:38:03 GMT","x-goog-generation":"1730457483026450","x-goog-metageneration":"1","x-goog-stored-content-length":"2213","x-goog-storage-class":"STANDARD","via":"1.1 google","accept-ranges":"bytes","cache-control":"public, max-age=31536000, immutable","age":"230","x-content-type-options":"nosniff","alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-robots-tag":"noindex","date":"Fri, 08 Nov 2024 10:06:11 GMT","server":"UploadServer","x-goog-hash":"crc32c=wc3qyg==,md5=ZnELlnACeIIF4eAeoqnemg==","etag":"\"66710b967002788205e1e01ea2a9de9a\"","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-status":"hit","access-control-expose-headers":"*","content-length":"2213","cross-origin-resource-policy":"cross-origin","expires":"Sat, 08 Nov 2025 10:06:11 GMT","x-goog-stored-content-encoding":"identity","content-type":"text/typescript","x-guploader-uploadid":"AHmUCY1TkleayWpcaR1GknL0dxZzHFsJajYmpM9v8Tgi5QdU2rYgVWRZ_zxgwssMUWRlLsXHR6A","access-control-allow-origin":"*","x-jsr-cache-id":"IAD"},"url":"https://jsr.io/@std/path/1.0.8/posix/basename.ts","time":1731060600}